/* eslint-disable max-lines */
import { HexCode, isHexCode } from '@/modules/kisweui/types'
import { rgbaToHex } from '@/modules/kisweui/utils'
import { KeyDict } from '@/types'

const THRESHOLD_BETWEEN_EPOCH_SECONDS_AND_MS = 3000000000

export class ValueLoader {
  static loadNumber (value: unknown): number {
    if (typeof value === 'number') {
      return value
    }
    throw new Error('Could not load data, expected value to be of type number')
  }
}

export class Loader {
  static loadEnum<T> (
    data: unknown, key: string, defaultValue: T|undefined = undefined, enumValues: T[]
  ): T {
    const value = this.loadEnumOrNull(data, key, defaultValue, enumValues)
    if (value) return value
    throw new Error(`Could not load data, expected ${key} to be of enum type`)
  }

  static loadEnumOrNull<T> (
    data: unknown, key: string, defaultValue: T|null|undefined = undefined, enumValues: T[]
  ): T|null {
    if (typeof data === 'object' && data !== null) {
       // @ts-ignore
      const value = data[key]
      if (enumValues.includes(value) || value === null) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of enum type or null`)
  }

  static loadOptionalEnum<T> (
    data: unknown, key: string, enumValues: T[], defaultValue?: T
  ): T|undefined {
    if (typeof data === 'object' && data !== null) {
       // @ts-ignore
      const value = data[key]
      if (enumValues.includes(value)) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return undefined
  }

  static loadString (data: unknown, key: string, defaultValue: string|undefined = undefined): string {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'string') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type string`)
  }

  static loadStringOrNull (data: unknown, key: string, defaultValue: string|null|undefined = undefined): string|null {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'string' || value === null) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type string|null`)
  }

  static loadOptionalString (data: unknown, key: string, defaultValue?: string): string|undefined {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'string') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return undefined
  }

  static loadHexCode (data: unknown, key: string, defaultValue: HexCode): HexCode {
    if (typeof data !== 'object' || data === null) {
      throw new Error(`Could not load data, expected ${data} to be of type object`)
    }
    // @ts-ignore
    const value: unknown = data[key]
    if (typeof value !== 'string') return defaultValue
    const convertedValue = rgbaToHex(value)
    if (isHexCode(convertedValue) && !convertedValue.includes('NAN')) return convertedValue
    else return defaultValue
  }

  static loadHexCodeOrNull (data: unknown, key: string, defaultValue: HexCode|null) {
    const value = this.loadHexCode(data, key, '#')
    if (value === '#') return defaultValue
    else return value
  }

  static loadBoolean (data: unknown, key: string, defaultValue: boolean|undefined = undefined): boolean {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'boolean') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type boolean`)
  }

  static loadBooleanOrNull (data: unknown, key: string,
                            defaultValue: boolean|null|undefined = undefined): boolean|null {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'boolean' || value === null) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type boolean or null`)
  }

  static loadOptionalBoolean (
    data: unknown, key: string, defaultValue?: boolean
  ): boolean|undefined {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'boolean') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return undefined
  }

  static loadNumber (data: unknown, key: string, defaultValue: number|undefined = undefined): number {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'number') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type number`)
  }

  static loadNumberOrNull (data: unknown, key: string, defaultValue: number|null|undefined = undefined): number|null {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'number' || value === null) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type number`)
  }

  static loadOptionalNumber (data: unknown, key: string, defaultValue?: number): number|undefined {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (typeof value === 'number') {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return undefined
  }

  // eslint-disable-next-line complexity
  static loadDate (data: unknown, key: string, defaultValue: Date|undefined = undefined): Date {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (value instanceof Date) return value
      // @ts-ignore
      if (typeof value === 'object' && value !== null && 'seconds' in value && value.seconds !== undefined) {
        // @ts-ignore
        return new Date(value.seconds * 1000 + value.nanoseconds / 1000000)
      }
      if (typeof value === 'string') {
        return new Date(value)
      }
      if (typeof value === 'number') {
        if (value < THRESHOLD_BETWEEN_EPOCH_SECONDS_AND_MS) return new Date(value * 1000)  // probably in seconds
        return new Date(value)
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be of type Date`)
  }

  static loadDateOrNull (data: unknown, key: string, defaultValue: Date|null|undefined = undefined): Date|null {
    if (data === null) return null
    try {
      return Loader.loadDate(data, key, defaultValue ?? undefined)
    } catch (error) {
      if (defaultValue === null) return null
      throw error
    }
  }

  static loadArray<T> (data: unknown, key: string, defaultValue: T[]|undefined = undefined): T[] {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (Array.isArray(value)) {
        return value as T[]
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be an Array`)
  }

  static loadArrayOrNull (data: unknown, key: string, defaultValue: unknown[]|null|undefined = undefined):
                         unknown[]|null {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (Array.isArray(value) || value === null) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    throw new Error(`Could not load data, expected ${key} to be an Array or null`)
  }

  static loadOptionalArray<T> (data: unknown, key: string, defaultValue?: T[]): T[]|undefined {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value = data[key]
      if (Array.isArray(value)) {
        return value
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue
    }
    return undefined
  }

  /** @deprecated Temporarily until better functions are available */
  static loadAndCast<T> (data: unknown, key: string, defaultValue: any = undefined): T {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const value: unknown = data[key]
      if (value !== undefined) {
        return value as T
      }
    }
    if (defaultValue !== undefined) {
      return defaultValue as T
    }
    throw new Error(`Could not load data, expected ${key} to be of specified type`)
  }

  // Constructs object of type T from value of a key in given data object. If key not found, return default if provided
  // Throw error if key not found, of wrong type or value is null
  // TODO: extend loadClass so it utilizes the static `.load` method of a class, since without it, it is prone to errors
  static loadClass<T> (
    data: unknown, key: string, loader: (data: unknown) => T, defaultValue: T|undefined = undefined
  ): T {
    const obj = Loader.loadClassOrNull(data, key, loader, defaultValue)
    // @ts-ignore
    if (obj !== null) return obj
    throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }

  // Constructs object of type T from value of a key in given data object. If key not found, return default if provided
  // Throw error if key not found or of wrong type. Null value is considered valid
  // eslint-disable-next-line complexity
  static loadClassOrNull<T> (
    data: unknown, key: string, loader: (data: unknown) => T, defaultValue: T|undefined = undefined
  ): T|null {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const rawObj: unknown = data[key]
      if (typeof rawObj === 'object' || rawObj === null) {
        let obj: T|null = null
        if (rawObj !== null) obj = loader(rawObj)
        return obj
      }
      if (rawObj === undefined && defaultValue !== undefined) return defaultValue
    } else if (data === undefined) {
      return defaultValue ?? null
    }
    throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }

  static loadOptionalClass<T> (
    data: unknown, key: string, loader: (data: unknown) => T, defaultValue?: T
  ): T|undefined {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const rawObj: unknown = data[key]
      if (typeof rawObj === 'object') {
        let obj: T|undefined
        if (rawObj !== null) obj = loader(rawObj)
        return obj
      }
      if (rawObj === undefined && defaultValue !== undefined) return defaultValue
    } else if (data === undefined) {
      return defaultValue
    }
    return undefined
  }

  // Constructs a Dictionary of objects of type T from value of a key in given data object.
  // If key not found, return default if provided
  // Throw error if key not found, of wrong type or value is null
  static loadKeyDict<T> (data: unknown, key: string, loader: (data: unknown) => T,
    defaultValue: KeyDict<T>|undefined = undefined): KeyDict<T> {
      const dict = Loader.loadKeyDictOrEmpty(data, key, loader, defaultValue)
      // @ts-ignore
      if (data?.[key] === undefined && defaultValue !== undefined) return defaultValue
      // @ts-ignore
      if (data !== undefined && data[key] !== null) return dict
      throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }

  // Constructs a Dictionary of objects of type T from value of a key in given data object.
  // If key not found, return default if provided
  // Throw error if key not found or of wrong type. Null value for key returns an empty dictionary
  // eslint-disable-next-line complexity
  static loadKeyDictOrEmpty<T> (data: unknown, key: string, loader: (data: unknown) => T,
    defaultValue: KeyDict<T>|undefined = undefined): KeyDict<T> {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const rawDict: unknown = data[key]
      if (typeof rawDict === 'object' || rawDict === null) {
        const dict: KeyDict<T> = {}
        // eslint-disable-next-line max-depth
        if (rawDict !== null) for (const [id, item] of Object.entries(rawDict)) dict[id] = loader(item)
        return dict
      }
    }
    if (defaultValue !== undefined) return defaultValue
    throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }


  // Constructs an array of objects of type T from value of a key in given data object.
  // If key not found, return default if provided
  // Throw error if key not found, of wrong type or value is null
  static loadList<T> (data: unknown, key: string, loader: (data: unknown) => T,
    defaultValue: T[]|undefined = undefined): T[] {
    const list = Loader.loadListOrEmpty(data, key, loader, defaultValue)
    // @ts-ignore
    if (data[key] !== null) return list
    throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }

  // Constructs an array of objects of type T from value of a key in given data object.
  // If key not found, return default if provided
  // Throw error if key not found or of wrong type. Null value for key returns an empty array
  // eslint-disable-next-line complexity
  static loadListOrEmpty<T> (data: unknown, key: string, loader: (data: unknown) => T,
    defaultValue: T[]|undefined = undefined): T[] {
    if (typeof data === 'object' && data !== null) {
      // @ts-ignore
      const rawList: unknown = data[key]
      if (Array.isArray(rawList) || rawList === null) {
        const list: T[] = []
        // eslint-disable-next-line max-depth
        if (rawList !== null) for (const item of Object.values(rawList)) list.push(loader(item))
        return list
      }
      if (rawList === undefined && defaultValue !== undefined) return defaultValue
    }
    throw new Error(`Unexpected type for key:${key} in snapshot:${JSON.stringify(data)}`)
  }
}
